import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-bootstrap';
import {
  sprayService,
  SprayProduct,
  SprayProductInput,
  ProductPhiType,
  DosePer100LUnit,
  DOSE_PER_100L_UNITS,
  ActiveIngredientUnit,
  ACTIVE_INGREDIENT_UNITS,
  PHI_TYPES,
  UsageRule,
  canonActiveIngredientUnit,
  canonDosePer100LUnit,
  canonPhiType,
} from '../../services/sprayService';
import { familyOf, UNITS_BY_FAMILY, MEASURE_UNITS } from '../../utils/units';
import UsageRulesEditor from './UsageRulesEditor';
import { listGlobalCropTypes, CropTypeNode } from '../../services/cropTypeService';
import { listPests, PestRef } from '../../services/pestService';
import { phiTypeLabel } from '../../utils/productDisplay';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { useFormDirty } from '../../hooks/useFormDirty';
import { useBeforeUnloadWarning } from '../../hooks/useBeforeUnloadWarning';
import { useConfirmDiscard } from '../../hooks/useConfirmDiscard';
import PdfUploader from './PdfUploader';
import { Field, FormSection, FormShell, FormShellSection, Notice, SaveBar, SaveBarStatus } from '../patterns';

interface Props {
  product?: SprayProduct;
  onSave: (product: SprayProduct) => void;
  onCancel: () => void;
}

/** Local row type: permits blank unit so the seed row is valid before the user selects one. */
type AiRow = { name: string; value: number; unit: ActiveIngredientUnit | '' };

const PETAL_DROP_80_DEFAULT = '80% Petal Drop';

const SprayProductForm: React.FC<Props> = ({ product, onSave, onCancel }) => {
  const { t } = useTranslation();
  const isEdit = !!product;

  const legacyBanner = (
    <Notice tone="info" className="mb-2" testId="legacy-deprecated-banner">
      {t('spray.products.legacyDeprecated.notice')}
    </Notice>
  );

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('spray.title'), to: '/protection' },
    { label: t('spray.products.title'), to: '/protection/products' },
    { label: isEdit
        ? t('spray.products.editTitle', { name: product?.productName ?? '' })
        : t('spray.pageHeader.actions.newProduct') },
  ]);

  const [productName, setProductName] = useState(product?.productName ?? '');
  const [lNumber, setLNumber] = useState(product?.lNumber ?? '');
  const initialAIs: AiRow[] = product
    ? (product.activeIngredients ?? []).map(ai => ({
        ...ai,
        unit: canonActiveIngredientUnit(ai.unit) as ActiveIngredientUnit | '',
      }))
    : [{ name: '', value: 0, unit: '' }];
  const [activeIngredients, setActiveIngredients] = useState<AiRow[]>(initialAIs);
  const [category, setCategory] = useState(product?.category ?? '');

  // PHI Type + Value selector (Phase 4). When type changes we reset the value
  // unless the existing value was authored for the same type, so the user
  // never sees stale data from a prior type.
  // Pre-compute canonicalised initial values so both useState and useFormDirty
  // baseline reference the same canonical string (dirty=false on first load).
  const initialPhiType = (canonPhiType(product?.phi?.type ?? '') as ProductPhiType | '') || '';
  const initialDosePer100LUnit = (canonDosePer100LUnit(product?.dosePer100LUnit ?? '') as DosePer100LUnit | '') || '';

  const [phiType, setPhiType] = useState<ProductPhiType | ''>(initialPhiType);
  const [phiValue, setPhiValue] = useState(product?.phi?.value ?? '');

  const [dosePer100L, setDosePer100L] = useState(product?.dosePer100L?.toString() ?? '');
  const [dosePer100LUnit, setDosePer100LUnit] = useState<DosePer100LUnit | ''>(initialDosePer100LUnit);
  const [stockUnit, setStockUnit] = useState(product?.stockUnit ?? '');
  const [pestTreated, setPestTreated] = useState(product?.pestTreated ?? '');
  const [formulation, setFormulation] = useState(product?.formulation ?? '');
  // PDF slots mirror the server record so upload/remove round-trips are
  // reflected in the UI without a full reload.
  const [labelPdf, setLabelPdf] = useState(product?.labelPdf);
  const [msdsPdf, setMsdsPdf] = useState(product?.msdsPdf);
  const [usageRules, setUsageRules] = useState<UsageRule[]>(product?.usageRules ?? []);

  const [cropOptions, setCropOptions] = useState<CropTypeNode[]>([]);
  const [pestOptions, setPestOptions] = useState<PestRef[]>([]);
  useEffect(() => {
    listGlobalCropTypes().then(setCropOptions).catch(() => setCropOptions([]));
    listPests().then(setPestOptions).catch(() => setPestOptions([]));
  }, []);

  const [nameError, setNameError] = useState('');
  const [apiError, setApiError] = useState('');
  const [saving, setSaving] = useState(false);

  const { dirty } = useFormDirty(
    {
      productName: product?.productName ?? '',
      lNumber: product?.lNumber ?? '',
      activeIngredients: JSON.stringify(initialAIs),
      category: product?.category ?? '',
      phiType: initialPhiType,
      phiValue: product?.phi?.value ?? '',
      dosePer100L: product?.dosePer100L?.toString() ?? '',
      dosePer100LUnit: initialDosePer100LUnit,
      stockUnit: product?.stockUnit ?? '',
      pestTreated: product?.pestTreated ?? '',
      formulation: product?.formulation ?? '',
      labelPdf: product?.labelPdf ?? null,
      msdsPdf: product?.msdsPdf ?? null,
      usageRules: JSON.stringify(product?.usageRules ?? []),
    },
    {
      productName,
      lNumber,
      activeIngredients: JSON.stringify(activeIngredients),
      category,
      phiType,
      phiValue,
      dosePer100L,
      dosePer100LUnit,
      stockUnit,
      pestTreated,
      formulation,
      labelPdf: labelPdf ?? null,
      msdsPdf: msdsPdf ?? null,
      usageRules: JSON.stringify(usageRules),
    },
  );
  useBeforeUnloadWarning(dirty);
  const confirmDiscard = useConfirmDiscard();

  const handlePhiTypeChange = (next: ProductPhiType | '') => {
    setPhiType(next);
    if (next === 'petal_drop_80') {
      setPhiValue(PETAL_DROP_80_DEFAULT);
    } else if (next === '' || next !== product?.phi?.type) {
      setPhiValue('');
    } else {
      setPhiValue(product?.phi?.value ?? '');
    }
  };

  const validate = () => {
    let valid = true;
    if (!productName.trim()) {
      setNameError(t('spray.products.nameRequired'));
      valid = false;
    } else {
      setNameError('');
    }
    return valid;
  };

  const handleAiChange = (i: number, patch: Partial<AiRow>) => {
    setActiveIngredients(prev => prev.map((ai, idx) => (idx === i ? { ...ai, ...patch } : ai)));
  };
  const handleAiAdd = () => {
    setActiveIngredients(prev => [...prev, { name: '', value: 0, unit: '' }]);
  };
  const handleAiRemove = (i: number) => {
    setActiveIngredients(prev => prev.filter((_, idx) => idx !== i));
  };

  const buildPhiPayload = (): { phi?: SprayProductInput['phi'] } => {
    if (!phiType) return {};
    const trimmedValue = phiValue.trim();
    if (!trimmedValue) return {};
    return { phi: { type: phiType, value: trimmedValue } };
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setApiError('');
    const data: SprayProductInput = {
      productName: productName.trim(),
      lNumber: lNumber.trim(),
      activeIngredients: activeIngredients
        .filter(ai => ai.name.trim() !== '' && ai.value > 0 && ai.unit !== '')
        .map(ai => ({ name: ai.name.trim(), value: ai.value, unit: ai.unit as ActiveIngredientUnit })),
      ...(category && { category }),
      ...buildPhiPayload(),
      ...(dosePer100L && { dosePer100L: Number(dosePer100L) }),
      ...(dosePer100LUnit && { dosePer100LUnit: dosePer100LUnit as DosePer100LUnit }),
      ...(stockUnit && { stockUnit }),
      ...(pestTreated && { pestTreated }),
      ...(formulation && { formulation }),
      usageRules,
    };
    try {
      const saved = product
        ? await sprayService.updateProduct(product._id, data)
        : await sprayService.createProduct(data);
      onSave(saved);
    } catch (err: any) {
      setApiError(err.message ?? t('spray.products.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void handleSave();
  };

  const phiValueInputType = phiType === 'days' ? 'number' : 'text';
  const phiValueDisabled = phiType === 'petal_drop_80';

  // Constrain the stock unit to the dose unit's family (mass↔mass / volume↔volume);
  // before a dose unit is chosen, offer every measure unit.
  const stockUnitOptions = (() => {
    const fam = familyOf(dosePer100LUnit);
    return fam ? UNITS_BY_FAMILY[fam] : MEASURE_UNITS;
  })();

  const titles = {
    identity: t('spray.products.sections.identity', { defaultValue: 'Identity' }),
    activeIngredient: t('spray.products.sections.activeIngredient', { defaultValue: 'Active ingredient' }),
    usageRules: t('spray.products.usageRules.sectionTitle', { defaultValue: 'Usage rules' }),
    stock: t('spray.products.sections.stock', { defaultValue: 'Stock' }),
    phi: t('spray.products.sections.phi', { defaultValue: 'PHI' }),
    dosage: t('spray.products.sections.dosage', { defaultValue: 'Dosage' }),
    documents: t('spray.products.sections.documents', { defaultValue: 'Documents' }),
  };

  // Stock sits ahead of PHI and Dosage: those two blocks are on their way out,
  // and the stock unit is not. Keeping it in its own section is what stops the
  // legacy banner from appearing to cover it.
  const sections: FormShellSection[] = [
    { id: 'product-identity', title: titles.identity },
    { id: 'product-active-ingredient', title: titles.activeIngredient },
    { id: 'product-usage-rules', title: titles.usageRules },
    { id: 'product-stock', title: titles.stock },
    { id: 'product-phi', title: titles.phi },
    { id: 'product-dosage', title: titles.dosage },
    ...(product?._id ? [{ id: 'product-documents', title: titles.documents }] : []),
  ];

  return (
    <div data-testid="spray-product-form">
      {apiError && (
        <Alert variant="danger" role="alert" className="mb-3">
          {apiError}
        </Alert>
      )}

      <FormShell
        sections={sections}
        onSubmit={handleSubmit}
        saveBar={
          <SaveBar
            status={
              saving
                ? ({ kind: 'saving' } as SaveBarStatus)
                : apiError
                  ? ({ kind: 'error', message: apiError } as SaveBarStatus)
                  : dirty
                    ? ({ kind: 'dirty' } as SaveBarStatus)
                    : null
            }
            primaryAction={{
              label: saving
                ? t('patterns.saveBar.primary.saving')
                : apiError
                  ? t('patterns.saveBar.primary.retry')
                  : t('spray.pageHeader.actions.saveAndClose'),
              onClick: handleSave,
              disabled: (!dirty && !apiError) || saving || !stockUnit,
            }}
            secondaryActions={[
              { label: t('common.cancel'), tone: 'subtle', onClick: () => { if (confirmDiscard(dirty)) onCancel(); }, disabled: saving },
            ]}
          />
        }
      >
        {product?.needsReview && (
          <Notice tone="warn" role="status" testId="needs-review-notice">
            {t('spray.products.needsReview.notice')}
          </Notice>
        )}

        <FormSection id="product-identity" title={titles.identity}>
          <div className="pat-field-grid pat-field-grid--2">
            <Field
              label={t('spray.products.name')}
              required
              controlId="productName"
              errorText={nameError || undefined}
            >
              <input
                id="productName"
                type="text"
                className="pat-input"
                value={productName}
                onChange={e => setProductName(e.target.value)}
                aria-invalid={Boolean(nameError) || undefined}
                aria-required="true"
              />
            </Field>
            <Field
              label={t('spray.products.lNumber')}
              controlId="lNumber"
              helpText={t('spray.products.lNumberHint')}
            >
              <input
                id="lNumber"
                type="text"
                className="pat-input"
                value={lNumber}
                onChange={e => setLNumber(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
              />
            </Field>
            <Field label={t('spray.products.category')} controlId="category">
              <input
                id="category"
                type="text"
                className="pat-input"
                value={category}
                onChange={e => setCategory(e.target.value)}
              />
            </Field>
            <Field label={t('spray.products.formulation')} controlId="formulation">
              <input
                id="formulation"
                type="text"
                className="pat-input"
                value={formulation}
                onChange={e => setFormulation(e.target.value)}
              />
            </Field>
            <div style={{ gridColumn: '1 / -1' }}>
              {legacyBanner}
            </div>
            <Field label={t('spray.products.pestTreated')} controlId="pestTreated">
              <select
                id="pestTreated"
                className="pat-input"
                value={pestTreated}
                onChange={e => setPestTreated(e.target.value)}
              >
                <option value="">—</option>
                {pestOptions.map(p => (
                  <option key={p.code} value={p.code}>{p.name}</option>
                ))}
              </select>
            </Field>
          </div>
        </FormSection>

        <FormSection id="product-active-ingredient" title={titles.activeIngredient}>
          {activeIngredients.length === 0 && (
            <div className="text-muted mb-2">{t('spray.products.noActiveIngredients')}</div>
          )}
          {activeIngredients.map((ai, i) => (
            <div
              key={i}
              data-testid={`ai-row-${i}`}
              className="pat-field-grid pat-field-grid--3 align-items-end mb-2"
            >
              <Field label={t('spray.products.activeIngredient')} controlId={`ai-name-${i}`}>
                <input
                  id={`ai-name-${i}`}
                  data-testid={`ai-row-${i}-name`}
                  type="text"
                  className="pat-input"
                  value={ai.name}
                  onChange={e => handleAiChange(i, { name: e.target.value })}
                />
              </Field>
              <Field label={t('spray.products.activeIngredientValue')} controlId={`ai-value-${i}`}>
                <input
                  id={`ai-value-${i}`}
                  data-testid={`ai-row-${i}-value`}
                  type="number"
                  className="pat-input"
                  value={Number.isFinite(ai.value) && ai.value !== 0 ? String(ai.value) : ''}
                  onChange={e => handleAiChange(i, { value: Number(e.target.value) || 0 })}
                />
              </Field>
              <Field label={t('spray.products.activeIngredientUnit')} controlId={`ai-unit-${i}`}>
                <div className="d-flex gap-2 align-items-center">
                  <select
                    id={`ai-unit-${i}`}
                    data-testid={`ai-row-${i}-unit`}
                    className="pat-input"
                    value={ai.unit}
                    onChange={e => handleAiChange(i, { unit: e.target.value as ActiveIngredientUnit })}
                  >
                    <option value="">{t('spray.products.activeIngredientUnitPlaceholder')}</option>
                    {ACTIVE_INGREDIENT_UNITS.map(u => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="pat-btn pat-btn--subtle"
                    aria-label={t('spray.products.removeActiveIngredient')}
                    data-testid={`ai-row-${i}-remove`}
                    onClick={() => handleAiRemove(i)}
                  >
                    <i className="bi bi-x-lg" aria-hidden="true" />
                  </button>
                </div>
              </Field>
            </div>
          ))}
          <button
            type="button"
            className="pat-btn pat-btn--subtle"
            onClick={handleAiAdd}
          >
            <i className="bi bi-plus-lg" aria-hidden="true" /> {t('spray.products.addActiveIngredient')}
          </button>
        </FormSection>

        <FormSection
          id="product-usage-rules"
          title={titles.usageRules}
          description={t('spray.products.usageRules.sectionHelp', { defaultValue: '' })}
        >
          <UsageRulesEditor value={usageRules} onChange={setUsageRules} cropOptions={cropOptions} pestOptions={pestOptions} />
        </FormSection>

        <FormSection
          id="product-stock"
          title={titles.stock}
          description={t('spray.products.sections.stockHelp', { defaultValue: '' })}
        >
          <div className="pat-field-grid pat-field-grid--2">
            <Field
              label={t('spray.products.stockUnit')}
              controlId="stockUnit"
              helpText={
                familyOf(dosePer100LUnit)
                  ? t('spray.products.stockUnitHint', { unit: dosePer100LUnit })
                  : undefined
              }
            >
              <select
                id="stockUnit"
                data-testid="stockUnit"
                className="pat-input"
                value={stockUnit}
                onChange={e => setStockUnit(e.target.value)}
              >
                <option value="">{t('spray.products.stockUnitPlaceholder')}</option>
                {stockUnitOptions.map(u => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </Field>
          </div>
        </FormSection>

        <FormSection id="product-phi" title={titles.phi}>
          {legacyBanner}
          <div className="pat-field-grid pat-field-grid--2">
            <Field label={t('spray.products.phiType')} controlId="phiType">
              <select
                id="phiType"
                className="pat-input"
                value={phiType}
                onChange={e => handlePhiTypeChange(e.target.value as ProductPhiType | '')}
              >
                <option value="">{t('spray.products.phiSelectType')}</option>
                {PHI_TYPES.map(type => (
                  <option key={type} value={type}>{phiTypeLabel(type, t)}</option>
                ))}
              </select>
            </Field>
            <Field label={t('spray.products.phiValue')} controlId="phiValue">
              <input
                id="phiValue"
                type={phiValueInputType}
                className="pat-input"
                value={phiValue}
                disabled={phiValueDisabled}
                onChange={e => setPhiValue(e.target.value)}
              />
            </Field>
          </div>
        </FormSection>

        <FormSection id="product-dosage" title={titles.dosage}>
          {legacyBanner}
          <div className="pat-field-grid pat-field-grid--2">
            <Field label={t('spray.products.dosePer100L')} controlId="dosePer100L">
              <input
                id="dosePer100L"
                type="number"
                className="pat-input"
                value={dosePer100L}
                onChange={e => setDosePer100L(e.target.value)}
              />
            </Field>
            <Field label={t('spray.products.dosePer100LUnit')} controlId="dosePer100LUnit">
              <select
                id="dosePer100LUnit"
                data-testid="dosePer100LUnit"
                className="pat-input"
                value={dosePer100LUnit}
                onChange={e => {
                  const next = e.target.value as DosePer100LUnit | '';
                  setDosePer100LUnit(next);
                  if (stockUnit && familyOf(next) && familyOf(stockUnit) !== familyOf(next)) {
                    setStockUnit('');
                  }
                }}
              >
                <option value="">{t('spray.products.dosePer100LUnitPlaceholder')}</option>
                {DOSE_PER_100L_UNITS.map(u => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </Field>
          </div>
        </FormSection>

        {product?._id && (
          <FormSection
            id="product-documents"
            title={titles.documents}
            description={t('spray.products.documentsHelp', {
              defaultValue: 'Upload product label and material safety data sheet.',
            })}
          >
            <div className="pat-field-grid pat-field-grid--2">
              <PdfUploader
                file={labelPdf}
                labelKey="spray.products.labelPdf"
                uploadPromptKey="spray.products.labelPdfUploadPrompt"
                onUpload={async file => {
                  const updated = await sprayService.uploadProductLabelPdf(product._id, file);
                  setLabelPdf(updated.labelPdf);
                }}
                onRemove={async () => {
                  const updated = await sprayService.deleteProductLabelPdf(product._id);
                  setLabelPdf(updated.labelPdf);
                }}
                onView={async () => {
                  const signedUrl = await sprayService.getProductLabelPdfSignedUrl(product._id);
                  window.open(signedUrl, '_blank', 'noopener,noreferrer');
                }}
              />
              <PdfUploader
                file={msdsPdf}
                labelKey="spray.products.msdsPdf"
                uploadPromptKey="spray.products.msdsPdfUploadPrompt"
                onUpload={async file => {
                  const updated = await sprayService.uploadProductMsdsPdf(product._id, file);
                  setMsdsPdf(updated.msdsPdf);
                }}
                onRemove={async () => {
                  const updated = await sprayService.deleteProductMsdsPdf(product._id);
                  setMsdsPdf(updated.msdsPdf);
                }}
                onView={async () => {
                  const signedUrl = await sprayService.getProductMsdsPdfSignedUrl(product._id);
                  window.open(signedUrl, '_blank', 'noopener,noreferrer');
                }}
              />
            </div>
          </FormSection>
        )}
      </FormShell>
    </div>
  );
};

export default SprayProductForm;
